import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface Medication {
  id: string
  name: string
  category: string
  form: string
  concentration: string
  default_dose_rate: number | null
  common_frequency: string
  stock_quantity: number
  reorder_threshold: number
  controlled_substance: boolean
  dea_schedule: string | null
  known_interactions: string
  contraindicated_species: string
  notes: string
  created_at: string
  updated_at: string
}

export interface StockAdjustment {
  id: string
  medication_id: string
  adjustment: number
  new_quantity: number
  reason: string
  adjusted_by: string
  created_at: string
}

export interface MedicationPrescription {
  id: string
  prescription_number: number
  dosage: string
  frequency: string
  status: string
  patient_id: string
  patient_name: string
}

interface MedicationsDetailState {
  medication: Medication | null
  stockAdjustments: StockAdjustment[]
  prescriptions: MedicationPrescription[]
  loading: boolean
  error: string | null
}

const initialState: MedicationsDetailState = {
  medication: null,
  stockAdjustments: [],
  prescriptions: [],
  loading: false,
  error: null,
}

export const fetchMedication = createAsyncThunk(
  'medications/fetchOne',
  async (id: string) => {
    const res = await fetch(`/.netlify/functions/medications/${id}`)
    if (!res.ok) throw new Error('Failed to fetch medication')
    return res.json() as Promise<Medication>
  }
)

export const updateMedication = createAsyncThunk(
  'medications/update',
  async ({ id, data }: { id: string; data: Partial<Medication> }) => {
    const res = await fetch(`/.netlify/functions/medications/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update medication')
    return res.json() as Promise<Medication>
  }
)

export const fetchStockAdjustments = createAsyncThunk(
  'medications/fetchStockAdjustments',
  async (medicationId: string) => {
    const res = await fetch(`/.netlify/functions/stock-adjustments?medication_id=${medicationId}`)
    if (!res.ok) throw new Error('Failed to fetch stock adjustments')
    return res.json() as Promise<StockAdjustment[]>
  }
)

export const addStockAdjustment = createAsyncThunk(
  'medications/addStockAdjustment',
  async (data: { medication_id: string; adjustment: number; new_quantity: number; reason: string; adjusted_by: string }) => {
    const res = await fetch('/.netlify/functions/stock-adjustments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to add stock adjustment')
    return res.json() as Promise<StockAdjustment>
  }
)

export const fetchMedicationPrescriptions = createAsyncThunk(
  'medications/fetchPrescriptions',
  async (medicationId: string) => {
    const res = await fetch(`/.netlify/functions/medication-prescriptions?medication_id=${medicationId}`)
    if (!res.ok) throw new Error('Failed to fetch prescriptions')
    return res.json() as Promise<MedicationPrescription[]>
  }
)

const medicationsSlice = createSlice({
  name: 'medications',
  initialState,
  reducers: {
    clearMedication(state) {
      state.medication = null
      state.stockAdjustments = []
      state.prescriptions = []
      state.loading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMedication.pending, (state) => {
        if (!state.medication) state.loading = true
      })
      .addCase(fetchMedication.fulfilled, (state, action) => {
        state.medication = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(fetchMedication.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load'
      })
      .addCase(updateMedication.fulfilled, (state, action) => {
        state.medication = action.payload
      })
      .addCase(fetchStockAdjustments.fulfilled, (state, action) => {
        state.stockAdjustments = action.payload
      })
      .addCase(addStockAdjustment.fulfilled, (state, action) => {
        state.stockAdjustments.unshift(action.payload)
        if (state.medication) {
          state.medication.stock_quantity = action.payload.new_quantity
        }
      })
      .addCase(fetchMedicationPrescriptions.fulfilled, (state, action) => {
        state.prescriptions = action.payload
      })
  },
})

export const { clearMedication } = medicationsSlice.actions
export default medicationsSlice.reducer
